import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMeSpaces } from '../hooks/useMeSpaces';
import type { SpaceRole, SpaceSummary } from '../types/spaces';

interface SpaceContextValue {
  /** The active space id, parsed from the `:spaceId` URL segment. */
  spaceId: string;
  /** The active space's row from `/me/spaces`, undefined while loading. */
  space: SpaceSummary | undefined;
  /** Every space the user can access (for the switcher). */
  spaces: SpaceSummary[];
  /** The user's role in the active space, undefined while loading. */
  role: SpaceRole | undefined;
  /** Convenience: the active space is the user's Personal Space. */
  isPersonal: boolean;
  /** True until `/me/spaces` resolves. */
  isLoading: boolean;
  /** True if the URL `:spaceId` resolved against `/me/spaces` and the user is not a member. */
  isForbidden: boolean;
}

export const SpaceContext = createContext<SpaceContextValue | null>(null);

/**
 * Like `useSpaceContext`, but returns `null` when no provider is mounted.
 * Use this from cross-surface components (e.g. assistant-ui pieces that
 * render under both space-scoped pages and the operator surface).
 */
export function useOptionalSpaceContext(): SpaceContextValue | null {
  return useContext(SpaceContext);
}

/**
 * Mounts inside the `/console/spaces/:spaceId/*` route subtree. Reads the
 * active space id from the URL and pairs it with the user's space list so
 * pages can render attribution and the switcher can render the menu.
 *
 * Operator routes do not mount this provider — they are space-agnostic.
 */
export function SpaceProvider({ children }: { children: ReactNode }) {
  const params = useParams<{ spaceId: string }>();
  const spaceId = params.spaceId ?? '';
  const { data, isLoading } = useMeSpaces();

  const value = useMemo<SpaceContextValue>(() => {
    const spaces = data?.items ?? [];
    const space = spaces.find((s) => s.spaceId === spaceId);
    return {
      spaceId,
      space,
      spaces,
      role: space?.role,
      isPersonal: space?.kind === 'personal',
      isLoading,
      isForbidden: !isLoading && spaces.length > 0 && !space,
    };
  }, [spaceId, data, isLoading]);

  return <SpaceContext.Provider value={value}>{children}</SpaceContext.Provider>;
}

/**
 * Read the active space. Throws if used outside `SpaceProvider`, since every
 * space-scoped page should be reachable only via the routed subtree.
 */
export function useSpaceContext(): SpaceContextValue {
  const ctx = useContext(SpaceContext);
  if (!ctx) {
    throw new Error('useSpaceContext must be used within a SpaceProvider');
  }
  return ctx;
}

/** Convenience for hooks that just need the current space id. */
export function useSpaceId(): string {
  return useSpaceContext().spaceId;
}
